import { FunctionComponent, useState, useEffect } from 'react';
import { Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import VerificationInput from 'react-verification-input';
import LoaderButton from '../../../components/common/LoaderButton';
import { useAppDispatch } from '../../../store';
import alert from '../../../utils/alert';
import { useLocation } from 'react-router-dom';
import {
  generateMfaCodeThunk,
  verifyMfaCodeThunk,
  getMfaPreferenceThunk,
  updateMfaPreferenceThunk,
} from '../api/mfaSlice';
import lockGlowIcon from '../../../assets/images/icons/lock-glow.svg';
import './MfaVerification.scss';
import { fetchAuthSession } from 'aws-amplify/auth';
import { createSessionApiThunk } from '../api/authSlice';
import { MFAMethodType } from '../../../components/common/MfaButton';

interface ResendButtonProps {
  onPress: () => void;
  label: string;
  isDisabled?: boolean;
}

const ResendButton: FunctionComponent<ResendButtonProps> = ({ onPress, label, isDisabled }) => (
  <button
    onClick={onPress}
    className={`resend-button ${isDisabled ? 'resend-button--disabled' : ''}`}
    disabled={isDisabled}
  >
    <span className={`resend-text ${isDisabled ? 'resend-text--disabled' : ''}`}>{label}</span>
  </button>
);

export const MfaVerification: FunctionComponent = () => {
  const { t } = useTranslation();
  const location = useLocation();

  const [mfaCode, setMfaCode] = useState('');
  const [timeLeft, setTimeLeft] = useState(60);
  const [selectedMethod, setSelectedMethod] = useState<MFAMethodType | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const dispatch = useAppDispatch();

  const loginEmail = location.state?.email;

  useEffect(() => {
    const fetchPreference = async () => {
      try {
        const result = await dispatch(getMfaPreferenceThunk(loginEmail)).unwrap();
        if (result.resource) {
          setSelectedMethod(result.resource);
        } else {
          setSelectedMethod('sms');
        }
      } catch (error) {
        alert.error(t('Something went wrong. Please try again.'));
      }
    };

    fetchPreference();
  }, [dispatch, loginEmail, t]);

  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;
    if (timeLeft > 0) {
      interval = setInterval(() => {
        setTimeLeft((prevTime) => prevTime - 1);
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [timeLeft]);

  const handleMfaSubmit = async () => {
    if (mfaCode.length !== 6) return;

    setIsSubmitting(true);
    try {
      await dispatch(verifyMfaCodeThunk({ email: loginEmail, code: mfaCode })).unwrap();

      const { accessToken } = (await fetchAuthSession()).tokens ?? {};

      const cognitoAccessToken = accessToken?.toString();

      await dispatch(
        createSessionApiThunk({
          accessToken: cognitoAccessToken || '',
        }),
      ).unwrap();
    } catch (error) {
      alert.error(t('Unable to verify. Please check your code and try again!'), {
        toastId: 'mfa-error',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const sendMfaCode = async (email: string, method: 'sms' | 'email') => {
    try {
      await dispatch(generateMfaCodeThunk(email)).unwrap();
      alert.success(
        method === 'sms'
          ? t('A new verification code has been sent to your phone')
          : t('A new verification code has been sent to your email'),
      );
    } catch (error) {
      alert.error(t('Failed to resend code'));
    }
  };

  const handleResend = async () => {
    if (!selectedMethod) {
      return;
    }
    setTimeLeft(60);
    await sendMfaCode(loginEmail, selectedMethod);
  };

  const handleMFAPreferenceChange = async () => {
    try {
      setTimeLeft(60);
      const newMethod = selectedMethod === 'sms' ? 'email' : 'sms';

      const result = await dispatch(
        updateMfaPreferenceThunk({
          email: loginEmail,
          mfaType: newMethod,
        }),
      ).unwrap();

      if (result.statusCode === 200) {
        setSelectedMethod(newMethod);
        await sendMfaCode(loginEmail, newMethod);
      }
    } catch (error) {
      alert.error(t('Failed to update verification method. Please try again.'));
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className='verification__container d-flex flex-column justify-content-center'>
      <div className='d-flex flex-column'>
        <img className='auth__page-icon' alt='lock-icon' src={lockGlowIcon} />
        <p className='auth__page-title'>{t('Verification')}</p>
        <p className='auth__help-text'>
          {selectedMethod === 'sms'
            ? t('Enter the verification code sent to your phone number.')
            : t('Enter the verification code sent to your email.')}
        </p>
      </div>
      <div className='auth__form--width'>
        <Form.Group controlId='mfaCode' className='auth__input-group'>
          <div className='d-flex justify-content-center'>
            <VerificationInput
              value={mfaCode}
              length={6}
              placeholder=''
              onChange={setMfaCode}
              classNames={{
                container: 'verification-input-container',
                character: 'otp-input',
                characterSelected: 'otp-input--selected',
              }}
              validChars='0-9'
              inputProps={{
                inputMode: 'numeric',
              }}
            />
          </div>
        </Form.Group>

        <LoaderButton
          className='w-100'
          onClick={handleMfaSubmit}
          isSubmitting={isSubmitting}
          disabled={mfaCode.length !== 6 || isSubmitting}
        >
          {t('Verify')}
        </LoaderButton>

        <div className='resend-container'>
          <h3 className='resend-title'>{t('Need a new code?')}</h3>
          <p className='timer-text'>
            {t('Resend code in {{time}}', { time: formatTime(timeLeft) })}
          </p>
          <div className='resend-buttons'>
            <ResendButton
              onPress={handleResend}
              label={selectedMethod === 'sms' ? t('Resend text') : t('Resend email')}
              isDisabled={timeLeft > 0}
            />
            <ResendButton
              onPress={handleMFAPreferenceChange}
              label={selectedMethod === 'sms' ? t('Email my code') : t('Text my code')}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default MfaVerification;
